// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type ListRenderItem,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { TaskFilterBar } from './TaskFilterBar';
import { TaskFolderRow } from './TaskFolderRow';
import { TaskRow } from './TaskRow';
import { TasksEmptyState } from './TasksEmptyState';
import { TasksSearchBar } from './TasksSearchBar';
import { TasksSelectionToolbar } from './TasksSelectionToolbar';
import { tasksStrings } from './strings';
import type {
  TaskFolder,
  TaskFolderId,
  TaskItem,
  TasksLoader,
  TasksSubscriber,
  TasksUpdater,
} from './types';
import { useTasksViewModel } from './useTasksViewModel';

export interface TasksScreenProps {
  readonly load: TasksLoader;
  readonly subscribe: TasksSubscriber;
  readonly update?: TasksUpdater | undefined;
  readonly onTaskPress?: ((task: TaskItem) => void) | undefined;
  readonly onNewTask?: (() => void) | undefined;
  readonly onBulkComplete?: ((ids: ReadonlySet<TaskItem['id']>) => void) | undefined;
  readonly onBulkDelete?: ((ids: ReadonlySet<TaskItem['id']>) => void) | undefined;
  readonly onBulkMove?: ((ids: ReadonlySet<TaskItem['id']>) => void) | undefined;
}

type ListEntry =
  | { readonly kind: 'folder'; readonly folder: TaskFolder }
  | { readonly kind: 'task'; readonly task: TaskItem };

const ROW_HEIGHT_ESTIMATE = 76;

/** Top-level Tasks screen. Owns the FlatList + selection toolbar overlay. */
export const TasksScreen: React.FC<TasksScreenProps> = ({
  load,
  subscribe,
  update,
  onTaskPress,
  onNewTask,
  onBulkComplete,
  onBulkDelete,
  onBulkMove,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const vm = useTasksViewModel({ load, subscribe, ...(update !== undefined ? { update } : {}) });
  const [nowMs] = useState(() => Date.now());

  const entries = useMemo<readonly ListEntry[]>(() => {
    if (vm.state.kind !== 'populated') {
      return [];
    }
    const list: ListEntry[] = [];
    const folderIndex = new Map<TaskFolderId, TaskFolder>();
    for (const folder of vm.state.folders) {
      folderIndex.set(folder.id, folder);
    }
    const tasksByFolder = new Map<TaskFolderId | null, TaskItem[]>();
    for (const task of vm.state.tasks) {
      const key = task.folderId ?? null;
      const bucket = tasksByFolder.get(key) ?? [];
      bucket.push(task);
      tasksByFolder.set(key, bucket);
    }
    // Top-level (no folder) tasks first.
    for (const task of tasksByFolder.get(null) ?? []) {
      list.push({ kind: 'task', task });
    }
    for (const folder of vm.state.folders) {
      list.push({ kind: 'folder', folder });
      if (vm.openFolderIds.has(folder.id)) {
        for (const task of tasksByFolder.get(folder.id) ?? []) {
          list.push({ kind: 'task', task });
        }
      }
    }
    return list;
  }, [vm.state, vm.openFolderIds]);

  const keyExtractor = useCallback((entry: ListEntry, index: number) => {
    return entry.kind === 'folder' ? `f:${entry.folder.id}` : `t:${entry.task.id}:${index}`;
  }, []);

  const renderItem: ListRenderItem<ListEntry> = useCallback(
    ({ item }) => {
      if (item.kind === 'folder') {
        return (
          <TaskFolderRow
            folder={item.folder}
            open={vm.openFolderIds.has(item.folder.id)}
            onToggle={() => vm.toggleFolderOpen(item.folder.id)}
          />
        );
      }
      return (
        <TaskRow
          task={item.task}
          selection={vm.selection}
          onToggleCompletion={vm.toggleCompletion}
          onPress={onTaskPress}
          onToggleSelection={(t) => vm.toggleSelection(t.id)}
          onLongPress={() => {
            if (vm.selection.mode === 'inactive') {
              vm.enterSelectionMode();
              vm.toggleSelection(item.task.id);
            }
          }}
          nowMs={nowMs}
        />
      );
    },
    [vm, onTaskPress, nowMs],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<ListEntry> | null | undefined, index: number) => ({
      length: ROW_HEIGHT_ESTIMATE,
      offset: ROW_HEIGHT_ESTIMATE * index,
      index,
    }),
    [],
  );

  const handleBulkComplete = useCallback(() => {
    if (vm.selection.mode === 'active') {
      onBulkComplete?.(vm.selection.selectedIds);
      vm.clearSelection();
    }
  }, [vm, onBulkComplete]);

  const handleBulkDelete = useCallback(() => {
    if (vm.selection.mode === 'active') {
      onBulkDelete?.(vm.selection.selectedIds);
      vm.clearSelection();
    }
  }, [vm, onBulkDelete]);

  const handleBulkMove = useCallback(() => {
    if (vm.selection.mode === 'active') {
      onBulkMove?.(vm.selection.selectedIds);
    }
  }, [vm, onBulkMove]);

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === 'loading') {
      return (
        <View style={styles.center} accessibilityRole="progressbar">
          <ActivityIndicator size="small" color={theme.colors.accent.indigo} />
        </View>
      );
    }
    if (vm.state.kind === 'error') {
      return (
        <View style={styles.center}>
          <Text style={[styles.errorTitle, { color: theme.colors.text.primary }]}>
            {tasksStrings.errorTitle}
          </Text>
          <Pressable
            onPress={vm.retry}
            accessibilityRole="button"
            accessibilityLabel={tasksStrings.errorRetry}
            style={({ pressed }) => [
              styles.retryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.retryText}>{tasksStrings.errorRetry}</Text>
          </Pressable>
        </View>
      );
    }
    if (vm.state.kind === 'empty') {
      return <TasksEmptyState filter={vm.state.filter} query={vm.state.query} />;
    }
    return (
      <FlatList<ListEntry>
        data={entries}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        windowSize={10}
        initialNumToRender={20}
        maxToRenderPerBatch={15}
        removeClippedSubviews={false}
      />
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}
      accessibilityLabel={tasksStrings.screenTitle}
    >
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {tasksStrings.screenTitle}
        </Text>
        <View style={styles.headerActions}>
          {vm.selection.mode === 'inactive' ? (
            <Pressable
              onPress={vm.enterSelectionMode}
              accessibilityRole="button"
              accessibilityLabel={tasksStrings.selection.enter}
              style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.headerAction]}
            >
              <Text style={[styles.headerActionText, { color: theme.colors.accent.indigo }]}>
                {tasksStrings.selection.enter}
              </Text>
            </Pressable>
          ) : null}
          {onNewTask !== undefined ? (
            <Pressable
              onPress={onNewTask}
              accessibilityRole="button"
              accessibilityLabel={tasksStrings.newTaskCta}
              style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.headerAction]}
            >
              <Text style={[styles.headerActionText, { color: theme.colors.accent.indigo }]}>
                {tasksStrings.newTaskCta}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>
      <TasksSearchBar value={vm.query} onChange={vm.setQuery} />
      <TaskFilterBar active={vm.filter} onChange={vm.setFilter} />
      {renderBody()}
      <TasksSelectionToolbar
        selection={vm.selection}
        onBulkComplete={handleBulkComplete}
        onBulkDelete={handleBulkDelete}
        onBulkMove={handleBulkMove}
        onClear={vm.clearSelection}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  headerActions: {
    flexDirection: 'row',
  } satisfies ViewStyle,
  headerAction: {
    marginLeft: 12,
  } satisfies ViewStyle,
  headerActionText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  title: {
    fontSize: 17,
    fontWeight: '600',
  } satisfies TextStyle,
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  } satisfies TextStyle,
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  } satisfies ViewStyle,
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
